import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Save,
  ArrowLeft,
  Loader2,
  Layout,
  Plus,
  Trash2,
  FileText,
  HelpCircle,
  Users,
  Briefcase,
  Layers,
  Code,
  Quote,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import ImageUpload from "../../components/common/ImageUpload";
import pageService from "../../services/pageService";
import usePortfolio from "../../hooks/usePortfolio";
import useCaseStudies from "../../hooks/useCaseStudies";
import MultiSelect from "../../components/common/MultiSelect";
import RichTextEditor from "../../components/common/RichTextEditor";

const PageForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!id);
  const { portfolios, loading: portfoliosLoading } = usePortfolio();
  const { caseStudies, loading: caseStudiesLoading } = useCaseStudies();

  const [formData, setFormData] = useState({
    page_name: "",
    slug: "",
    category: "Capabilities",
    meta_title: "",
    meta_description: "",
    section1_hero: {
      title: "",
      subtitle: "",
      description: "",
      brand_image: "",
      button1: "",
      button1_link: "",
      button2: "",
      button2_link: "",
      benefits: [{ title: "", description: "" }],
    },
    section2_real_problem: {
      title: "",
      title2: "",
      subtitle: "",
      problems: [{ title: "", description: "" }],
    },
    revenue_ready_system: {
      title: "",
      description: "",
      cards: [{ image: "", title: "", description: "" }],
    },
    launch_process: {
      title: "",
      description: "",
      steps: [{ number: "", title: "", description: "" }],
    },
    strategy_execution: {
      title: "",
      description: "",
      footer_note: "",
      stats: [{ value: "", label: "", description: "", source: "" }],
    },
    expertise: {
      title: "",
      description: "",
      mistakes: [{ title: "", description: "" }],
    },
    our_promise: {
      title: "",
      promises: [{ image: "", title: "", copy: "" }],
    },
    section3_who_this_is_for: [
      {
        title: "",
        subtitle: "",
        benefits: [""],
      },
    ],
    section4_brands: [],
    section4_case_studies: [],
    section5_faq: {
      title: "",
      description: "",
      questions: [{ question: "", answer: "" }],
    },
    testimonial: {
      quote: "",
      author: "",
    },
    trusted_partners: {
      title: "",
      description: "",
      partners: [],
    },
    cta: {
      title: "",
      description: "",
      button_text: "",
      button_link: "",
      footer_note: "",
      bg_image: "",
    },
    custom_script: "",
  });

  useEffect(() => {
    if (id) {
      const loadData = async () => {
        try {
          const response = await pageService.getPageById(id);
          if (response.success) {
            const data = response.data;

            // Normalize Section 3 (Who this is for) - Convert object to array if needed
            if (
              data.section3_who_this_is_for &&
              !Array.isArray(data.section3_who_this_is_for)
            ) {
              data.section3_who_this_is_for = [data.section3_who_this_is_for];
            }

            if (!data.section1_hero) {
              data.section1_hero = {
                title: "",
                subtitle: "",
                description: "",
                brand_image: "",
                button1: "",
                button1_link: "",
                button2: "",
                button2_link: "",
                benefits: [{ title: "", description: "" }],
              };
            } else {
              data.section1_hero = {
                title: data.section1_hero.title || "",
                subtitle: data.section1_hero.subtitle || "",
                description: data.section1_hero.description || "",
                brand_image: data.section1_hero.brand_image || "",
                button1: data.section1_hero.button1 || "",
                button1_link: data.section1_hero.button1_link || "",
                button2: data.section1_hero.button2 || "",
                button2_link: data.section1_hero.button2_link || "",
                benefits:
                  Array.isArray(data.section1_hero.benefits) &&
                  data.section1_hero.benefits.length > 0
                    ? data.section1_hero.benefits
                    : [{ title: "", description: "" }],
              };
            }

            // Normalize Section 5 (FAQ) - Convert array to object if needed
            if (data.section5_faq && Array.isArray(data.section5_faq)) {
              data.section5_faq = {
                title: "",
                description: "",
                questions: data.section5_faq,
              };
            }

            // Normalize Section 4 (Brands)
            if (data.section4_brands) {
              if (Array.isArray(data.section4_brands)) {
                data.section4_brands = data.section4_brands.filter(
                  (b) => b && b.name,
                );
              } else {
                data.section4_brands = [];
              }
            } else {
              data.section4_brands = [];
            }

            // Normalize Section 4 Case Studies
            if (data.section4_case_studies) {
              if (Array.isArray(data.section4_case_studies)) {
                data.section4_case_studies = data.section4_case_studies.filter(
                  (c) => c && c.title,
                );
              } else {
                data.section4_case_studies = [];
              }
            } else {
              data.section4_case_studies = [];
            }

            // Normalize revenue_ready_system
            if (!data.revenue_ready_system) {
              data.revenue_ready_system = {
                title: "",
                description: "",
                cards: [{ image: "", title: "", description: "" }],
              };
            } else if (
              data.revenue_ready_system &&
              !data.revenue_ready_system.cards
            ) {
              data.revenue_ready_system.cards = [
                { image: "", title: "", description: "" },
              ];
            }

            // Normalize launch_process
            if (!data.launch_process) {
              data.launch_process = {
                title: "",
                description: "",
                steps: [{ number: "", title: "", description: "" }],
              };
            } else if (data.launch_process && !data.launch_process.steps) {
              data.launch_process.steps = [
                { number: "", title: "", description: "" },
              ];
            }

            // Normalize strategy_execution
            if (!data.strategy_execution) {
              data.strategy_execution = {
                title: "",
                description: "",
                footer_note: "",
                stats: [{ value: "", label: "", description: "", source: "" }],
              };
            } else if (
              data.strategy_execution &&
              !data.strategy_execution.stats
            ) {
              data.strategy_execution.stats = [
                { value: "", label: "", description: "", source: "" },
              ];
            }

            // Normalize expertise
            if (!data.expertise) {
              data.expertise = {
                title: "",
                description: "",
                mistakes: [{ title: "", description: "" }],
              };
            } else if (data.expertise && !data.expertise.mistakes) {
              data.expertise.mistakes = [{ title: "", description: "" }];
            }

            // Normalize our_promise
            if (!data.our_promise) {
              data.our_promise = {
                title: "",
                promises: [{ image: "", title: "", copy: "" }],
              };
            } else if (data.our_promise && !data.our_promise.promises) {
              data.our_promise.promises = [{ image: "", title: "", copy: "" }];
            }

            // Normalize testimonial
            if (!data.testimonial) {
              data.testimonial = {
                quote: "",
                author: "",
              };
            }

            // Normalize trusted_partners
            if (!data.trusted_partners) {
              data.trusted_partners = {
                title: "",
                description: "",
                partners: [],
              };
            } else if (
              data.trusted_partners &&
              !data.trusted_partners.partners
            ) {
              data.trusted_partners.partners = [];
            }

            // Normalize cta
            if (!data.cta) {
              data.cta = {
                title: "",
                description: "",
                button_text: "",
                button_link: "",
                footer_note: "",
                bg_image: "",
              };
            }

            setFormData(data);
          } else {
            toast.error(response.message || "Failed to load page");
            navigate("/pages");
          }
        } catch (error) {
          toast.error("An error occurred while loading page");
          navigate("/pages");
        } finally {
          setIsLoading(false);
        }
      };
      loadData();
    }
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.page_name) {
      toast.error("Page name is required");
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      if (id) {
        response = await pageService.updatePage(id, formData);
      } else {
        response = await pageService.createPage(formData);
      }

      if (response.success) {
        toast.success(
          id ? "Page updated successfully" : "Page created successfully",
        );
        navigate("/pages");
      } else {
        toast.error(response.message || "Failed to save page");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An error occurred while saving the page",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddMore = (section, field, defaultValue) => {
    setFormData((prev) => {
      const newData = { ...prev };
      if (section && field) {
        newData[section] = {
          ...newData[section],
          [field]: [...newData[section][field], defaultValue],
        };
      } else if (section) {
        newData[section] = [...newData[section], defaultValue];
      }
      return newData;
    });
  };

  const handleRemove = (section, field, index) => {
    setFormData((prev) => {
      const newData = { ...prev };
      if (section && field) {
        const list = [...newData[section][field]];
        list.splice(index, 1);
        newData[section] = {
          ...newData[section],
          [field]: list,
        };
      } else if (section) {
        const list = [...newData[section]];
        list.splice(index, 1);
        newData[section] = list;
      }
      return newData;
    });
  };

  const handleNestedChange = (section, field, index, subField, value) => {
    setFormData((prev) => {
      const newData = { ...prev };
      if (section && field) {
        const list = [...newData[section][field]];
        if (subField) {
          list[index] = { ...list[index], [subField]: value };
        } else {
          list[index] = value;
        }
        newData[section] = {
          ...newData[section],
          [field]: list,
        };
      } else if (section) {
        const list = [...newData[section]];
        if (subField) {
          list[index] = { ...list[index], [subField]: value };
        } else {
          list[index] = value;
        }
        newData[section] = list;
      }
      return newData;
    });
  };

  const handlePortfolioChange = (selectedIds) => {
    const updatedBrands = selectedIds
      .map((id) => {
        const p = portfolios.find((port) => String(port.id) === String(id));
        if (p) {
          return {
            name: p.name,
            image: p.image_url,
            link: p.link || "",
          };
        }
        return null;
      })
      .filter(Boolean);

    setFormData((prev) => ({
      ...prev,
      section4_brands: updatedBrands,
    }));
  };

  const handleRemovePortfolio = (brandName) => {
    setFormData((prev) => ({
      ...prev,
      section4_brands: (prev.section4_brands || []).filter(
        (b) => b.name !== brandName,
      ),
    }));
  };

  const handleCaseStudyChange = (selectedIds) => {
    const updatedCaseStudies = selectedIds
      .map((id) => {
        const cs = caseStudies.find((c) => String(c.id) === String(id));
        if (cs) {
          return {
            id: cs.id,
            title: cs.sort_data?.title || cs.banner?.title || cs.page_url,
            sub_title: cs.sort_data?.sub_title || cs.banner?.sub_title || "",
            image: cs.sort_data?.image || cs.banner?.image || "",
            page_url: cs.page_url,
          };
        }
        return null;
      })
      .filter(Boolean);

    setFormData((prev) => ({
      ...prev,
      section4_case_studies: updatedCaseStudies,
    }));
  };

  const handleRemoveCaseStudy = (title) => {
    setFormData((prev) => ({
      ...prev,
      section4_case_studies: (prev.section4_case_studies || []).filter(
        (c) => c.title !== title,
      ),
    }));
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-primary mb-4" size={40} />
        <p className="text-slate-500 font-medium">Loading Page Editor...</p>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-slate-50 min-h-screen">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/pages")}
            className="rounded-full h-10 w-10 p-0 hover:bg-slate-100"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              {id ? "Edit Page" : "Add New Page"}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/pages")}
            disabled={isSubmitting}
            className="rounded-full px-6"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary text-secondary rounded-full px-8 gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <Save size={18} />
            )}
            {id ? "Save Changes" : "Create Page"}
          </Button>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="max-w-5xl mx-auto mt-8 space-y-8 px-6"
      >
        {/* General Info */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="text-primary" size={24} />
            <h2 className="text-xl font-bold">General Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="page_name">Page Name *</Label>
              <Input
                id="page_name"
                value={formData.page_name}
                onChange={(e) => {
                  const name = e.target.value;
                  const slug = name
                    .toLowerCase()
                    .trim()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "");
                  setFormData({ ...formData, page_name: name, slug: slug });
                }}
                placeholder="e.g. D2C Ecommerce Launch"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData({ ...formData, category: value })
                }
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Capabilities">Capabilities</SelectItem>
                  <SelectItem value="Industries">Industries</SelectItem>
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug (URL)</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                placeholder="slug-will-be-auto-generated"
              />
              <p className="text-xs text-slate-500">
                Auto-generated from name. You can manually edit it.
              </p>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="meta_title">Meta Title (SEO)</Label>
              <Input
                id="meta_title"
                value={formData.meta_title || ""}
                onChange={(e) =>
                  setFormData({ ...formData, meta_title: e.target.value })
                }
                placeholder="Custom browser tab title. Default falls back to Page Name."
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="meta_description">Meta Description (SEO)</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description || ""}
                onChange={(e) =>
                  setFormData({ ...formData, meta_description: e.target.value })
                }
                placeholder="Custom SEO meta snippet description. Default falls back to hero description."
                className="h-20"
              />
            </div>
          </div>
        </div>

        {/* Section 1: Hero */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Section 1: Hero Section</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.section1_hero.title}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    section1_hero: {
                      ...formData.section1_hero,
                      title: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Input
                value={formData.section1_hero.subtitle}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    section1_hero: {
                      ...formData.section1_hero,
                      subtitle: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.section1_hero.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    section1_hero: {
                      ...formData.section1_hero,
                      description: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <ImageUpload
                label="Brand Image"
                value={formData.section1_hero.brand_image || ""}
                onChange={(url) =>
                  setFormData({
                    ...formData,
                    section1_hero: {
                      ...formData.section1_hero,
                      brand_image: url,
                    },
                  })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Button 1 Text</Label>
                <Input
                  value={formData.section1_hero.button1}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      section1_hero: {
                        ...formData.section1_hero,
                        button1: e.target.value,
                      },
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Button 1 Link</Label>
                <Input
                  value={formData.section1_hero.button1_link}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      section1_hero: {
                        ...formData.section1_hero,
                        button1_link: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Button 2 Text</Label>
                <Input
                  value={formData.section1_hero.button2}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      section1_hero: {
                        ...formData.section1_hero,
                        button2: e.target.value,
                      },
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Button 2 Link</Label>
                <Input
                  value={formData.section1_hero.button2_link}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      section1_hero: {
                        ...formData.section1_hero,
                        button2_link: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">Hero Benefits</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  handleAddMore("section1_hero", "benefits", {
                    title: "",
                    description: "",
                  })
                }
              >
                <Plus size={16} className="mr-1" /> Add Benefit
              </Button>
            </div>
            <div className="space-y-4">
              {formData.section1_hero.benefits.map((benefit, idx) => (
                <div
                  key={idx}
                  className="flex gap-4 p-4 bg-slate-50 rounded-xl relative border border-slate-100"
                >
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Benefit Title"
                      value={benefit.title}
                      onChange={(e) =>
                        handleNestedChange(
                          "section1_hero",
                          "benefits",
                          idx,
                          "title",
                          e.target.value,
                        )
                      }
                    />
                    <Input
                      placeholder="Benefit Description"
                      value={benefit.description}
                      onChange={(e) =>
                        handleNestedChange(
                          "section1_hero",
                          "benefits",
                          idx,
                          "description",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      handleRemove("section1_hero", "benefits", idx)
                    }
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-10 w-10 p-0 rounded-full"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 2: Real Problem */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Section 2: Real Problem</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.section2_real_problem.title}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    section2_real_problem: {
                      ...formData.section2_real_problem,
                      title: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Title 2</Label>
              <Input
                value={formData.section2_real_problem.title2}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    section2_real_problem: {
                      ...formData.section2_real_problem,
                      title2: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label>Subtitle</Label>
              <Input
                value={formData.section2_real_problem.subtitle}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    section2_real_problem: {
                      ...formData.section2_real_problem,
                      subtitle: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">Problems</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  handleAddMore("section2_real_problem", "problems", {
                    title: "",
                    description: "",
                  })
                }
              >
                <Plus size={16} className="mr-1" /> Add Problem
              </Button>
            </div>
            <div className="space-y-4">
              {formData.section2_real_problem.problems.map((problem, idx) => (
                <div
                  key={idx}
                  className="flex gap-4 p-4 bg-slate-50 rounded-xl relative border border-slate-100"
                >
                  <div className="flex-1 space-y-4">
                    <Input
                      placeholder="Problem Title"
                      value={problem.title}
                      onChange={(e) =>
                        handleNestedChange(
                          "section2_real_problem",
                          "problems",
                          idx,
                          "title",
                          e.target.value,
                        )
                      }
                    />
                    <RichTextEditor
                      placeholder="Problem Description"
                      value={problem.description}
                      onChange={(val) =>
                        handleNestedChange(
                          "section2_real_problem",
                          "problems",
                          idx,
                          "description",
                          val,
                        )
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      handleRemove("section2_real_problem", "problems", idx)
                    }
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-10 w-10 p-0 rounded-full"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 3: Revenue Ready System Section */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="text-primary" size={24} />
            <h2 className="text-xl font-bold">
              Section 3: Revenue Ready System
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.revenue_ready_system?.title || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    revenue_ready_system: {
                      ...(formData.revenue_ready_system || {}),
                      title: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.revenue_ready_system?.description || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    revenue_ready_system: {
                      ...(formData.revenue_ready_system || {}),
                      description: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">System Cards</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentCards =
                    formData.revenue_ready_system?.cards || [];
                  setFormData({
                    ...formData,
                    revenue_ready_system: {
                      ...(formData.revenue_ready_system || {}),
                      cards: [
                        ...currentCards,
                        { image: "", title: "", description: "" },
                      ],
                    },
                  });
                }}
              >
                <Plus size={16} className="mr-1" /> Add Card
              </Button>
            </div>
            <div className="space-y-6">
              {(formData.revenue_ready_system?.cards || []).map((card, idx) => (
                <div
                  key={idx}
                  className="p-6 bg-slate-50 rounded-2xl border border-slate-200 relative space-y-4"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const currentCards = [
                        ...(formData.revenue_ready_system?.cards || []),
                      ];
                      currentCards.splice(idx, 1);
                      setFormData({
                        ...formData,
                        revenue_ready_system: {
                          ...(formData.revenue_ready_system || {}),
                          cards: currentCards,
                        },
                      });
                    }}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                  >
                    <Trash2 size={18} />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Card Title</Label>
                      <Input
                        value={card.title || ""}
                        onChange={(e) => {
                          const currentCards = [
                            ...(formData.revenue_ready_system?.cards || []),
                          ];
                          currentCards[idx] = {
                            ...currentCards[idx],
                            title: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            revenue_ready_system: {
                              ...(formData.revenue_ready_system || {}),
                              cards: currentCards,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Card Description</Label>
                      <Textarea
                        value={card.description || ""}
                        onChange={(e) => {
                          const currentCards = [
                            ...(formData.revenue_ready_system?.cards || []),
                          ];
                          currentCards[idx] = {
                            ...currentCards[idx],
                            description: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            revenue_ready_system: {
                              ...(formData.revenue_ready_system || {}),
                              cards: currentCards,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <ImageUpload
                      label="Card Icon/Image"
                      value={card.image || ""}
                      onChange={(url) => {
                        const currentCards = [
                          ...(formData.revenue_ready_system?.cards || []),
                        ];
                        currentCards[idx] = {
                          ...currentCards[idx],
                          image: url,
                        };
                        setFormData({
                          ...formData,
                          revenue_ready_system: {
                            ...(formData.revenue_ready_system || {}),
                            cards: currentCards,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 4: Launch Process Section */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Section 4: Launch Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.launch_process?.title || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    launch_process: {
                      ...(formData.launch_process || {}),
                      title: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.launch_process?.description || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    launch_process: {
                      ...(formData.launch_process || {}),
                      description: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">Steps</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentSteps = formData.launch_process?.steps || [];
                  setFormData({
                    ...formData,
                    launch_process: {
                      ...(formData.launch_process || {}),
                      steps: [
                        ...currentSteps,
                        { number: "", title: "", description: "" },
                      ],
                    },
                  });
                }}
              >
                <Plus size={16} className="mr-1" /> Add Step
              </Button>
            </div>
            <div className="space-y-6">
              {(formData.launch_process?.steps || []).map((step, idx) => (
                <div
                  key={idx}
                  className="p-6 bg-slate-50 rounded-2xl border border-slate-200 relative space-y-4"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const currentSteps = [
                        ...(formData.launch_process?.steps || []),
                      ];
                      currentSteps.splice(idx, 1);
                      setFormData({
                        ...formData,
                        launch_process: {
                          ...(formData.launch_process || {}),
                          steps: currentSteps,
                        },
                      });
                    }}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                  >
                    <Trash2 size={18} />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label>Step Number</Label>
                      <Input
                        placeholder="e.g. 01"
                        value={step.number || ""}
                        onChange={(e) => {
                          const currentSteps = [
                            ...(formData.launch_process?.steps || []),
                          ];
                          currentSteps[idx] = {
                            ...currentSteps[idx],
                            number: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            launch_process: {
                              ...(formData.launch_process || {}),
                              steps: currentSteps,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Step Title</Label>
                      <Input
                        value={step.title || ""}
                        onChange={(e) => {
                          const currentSteps = [
                            ...(formData.launch_process?.steps || []),
                          ];
                          currentSteps[idx] = {
                            ...currentSteps[idx],
                            title: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            launch_process: {
                              ...(formData.launch_process || {}),
                              steps: currentSteps,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Step Description</Label>
                      <Textarea
                        value={step.description || ""}
                        onChange={(e) => {
                          const currentSteps = [
                            ...(formData.launch_process?.steps || []),
                          ];
                          currentSteps[idx] = {
                            ...currentSteps[idx],
                            description: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            launch_process: {
                              ...(formData.launch_process || {}),
                              steps: currentSteps,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 5: Who this is for */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="text-primary" size={24} />
              <h2 className="text-xl font-bold">Section 5: Who this is for</h2>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                handleAddMore("section3_who_this_is_for", null, {
                  title: "",
                  subtitle: "",
                  benefits: [""],
                })
              }
            >
              <Plus size={16} className="mr-1" /> Add Card
            </Button>
          </div>

          <div className="space-y-8">
            {formData.section3_who_this_is_for.map((card, cardIdx) => (
              <div
                key={cardIdx}
                className="p-6 bg-slate-50 rounded-2xl border border-slate-200 relative space-y-6"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    handleRemove("section3_who_this_is_for", null, cardIdx)
                  }
                  className="absolute top-2 right-2 text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                >
                  <Trash2 size={18} />
                </Button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Card Title</Label>
                    <Input
                      value={card.title}
                      onChange={(e) => {
                        const newList = [...formData.section3_who_this_is_for];
                        newList[cardIdx] = {
                          ...newList[cardIdx],
                          title: e.target.value,
                        };
                        setFormData({
                          ...formData,
                          section3_who_this_is_for: newList,
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Card Subtitle</Label>
                    <Input
                      value={card.subtitle}
                      onChange={(e) => {
                        const newList = [...formData.section3_who_this_is_for];
                        newList[cardIdx] = {
                          ...newList[cardIdx],
                          subtitle: e.target.value,
                        };
                        setFormData({
                          ...formData,
                          section3_who_this_is_for: newList,
                        });
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-bold text-slate-600">
                      Benefits List
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-primary h-8"
                      onClick={() => {
                        const newList = [...formData.section3_who_this_is_for];
                        newList[cardIdx] = {
                          ...newList[cardIdx],
                          benefits: [...newList[cardIdx].benefits, ""],
                        };
                        setFormData({
                          ...formData,
                          section3_who_this_is_for: newList,
                        });
                      }}
                    >
                      <Plus size={14} className="mr-1" /> Add Benefit
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {card.benefits.map((benefit, benefitIdx) => (
                      <div key={benefitIdx} className="flex gap-2 items-center">
                        <Input
                          placeholder="Benefit text"
                          value={benefit}
                          onChange={(e) => {
                            const newList = [
                              ...formData.section3_who_this_is_for,
                            ];
                            const newBenefits = [...newList[cardIdx].benefits];
                            newBenefits[benefitIdx] = e.target.value;
                            newList[cardIdx] = {
                              ...newList[cardIdx],
                              benefits: newBenefits,
                            };
                            setFormData({
                              ...formData,
                              section3_who_this_is_for: newList,
                            });
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newList = [
                              ...formData.section3_who_this_is_for,
                            ];
                            const newBenefits = [...newList[cardIdx].benefits];
                            newBenefits.splice(benefitIdx, 1);
                            newList[cardIdx] = {
                              ...newList[cardIdx],
                              benefits: newBenefits,
                            };
                            setFormData({
                              ...formData,
                              section3_who_this_is_for: newList,
                            });
                          }}
                          className="text-red-400 hover:text-red-600 p-0 h-8 w-8"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 6: Strategy Meets Execution Section */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="text-primary" size={24} />
            <h2 className="text-xl font-bold">
              Section 6: Strategy Meets Execution
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.strategy_execution?.title || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    strategy_execution: {
                      ...(formData.strategy_execution || {}),
                      title: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.strategy_execution?.description || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    strategy_execution: {
                      ...(formData.strategy_execution || {}),
                      description: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Footer Note</Label>
              <Input
                placeholder="e.g. * Verified client results, available on request"
                value={formData.strategy_execution?.footer_note || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    strategy_execution: {
                      ...(formData.strategy_execution || {}),
                      footer_note: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">Stats</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentStats = formData.strategy_execution?.stats || [];
                  setFormData({
                    ...formData,
                    strategy_execution: {
                      ...(formData.strategy_execution || {}),
                      stats: [
                        ...currentStats,
                        { value: "", label: "", description: "", source: "" },
                      ],
                    },
                  });
                }}
              >
                <Plus size={16} className="mr-1" /> Add Stat
              </Button>
            </div>
            <div className="space-y-6">
              {(formData.strategy_execution?.stats || []).map((stat, idx) => (
                <div
                  key={idx}
                  className="p-6 bg-slate-50 rounded-2xl border border-slate-200 relative space-y-4"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const currentStats = [
                        ...(formData.strategy_execution?.stats || []),
                      ];
                      currentStats.splice(idx, 1);
                      setFormData({
                        ...formData,
                        strategy_execution: {
                          ...(formData.strategy_execution || {}),
                          stats: currentStats,
                        },
                      });
                    }}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                  >
                    <Trash2 size={18} />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Stat Value (e.g. 3.8x, 68%)</Label>
                      <Input
                        value={stat.value || ""}
                        onChange={(e) => {
                          const currentStats = [
                            ...(formData.strategy_execution?.stats || []),
                          ];
                          currentStats[idx] = {
                            ...currentStats[idx],
                            value: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            strategy_execution: {
                              ...(formData.strategy_execution || {}),
                              stats: currentStats,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stat Label</Label>
                      <Input
                        value={stat.label || ""}
                        onChange={(e) => {
                          const currentStats = [
                            ...(formData.strategy_execution?.stats || []),
                          ];
                          currentStats[idx] = {
                            ...currentStats[idx],
                            label: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            strategy_execution: {
                              ...(formData.strategy_execution || {}),
                              stats: currentStats,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Stat Description</Label>
                      <Textarea
                        value={stat.description || ""}
                        onChange={(e) => {
                          const currentStats = [
                            ...(formData.strategy_execution?.stats || []),
                          ];
                          currentStats[idx] = {
                            ...currentStats[idx],
                            description: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            strategy_execution: {
                              ...(formData.strategy_execution || {}),
                              stats: currentStats,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Stat Source/Client Name</Label>
                      <Input
                        value={stat.source || ""}
                        onChange={(e) => {
                          const currentStats = [
                            ...(formData.strategy_execution?.stats || []),
                          ];
                          currentStats[idx] = {
                            ...currentStats[idx],
                            source: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            strategy_execution: {
                              ...(formData.strategy_execution || {}),
                              stats: currentStats,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 7: Expertise Section */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Section 7: Expertise</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.expertise?.title || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expertise: {
                      ...(formData.expertise || {}),
                      title: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.expertise?.description || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expertise: {
                      ...(formData.expertise || {}),
                      description: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">
                Mistakes / Expertise Points
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentMistakes = formData.expertise?.mistakes || [];
                  setFormData({
                    ...formData,
                    expertise: {
                      ...(formData.expertise || {}),
                      mistakes: [
                        ...currentMistakes,
                        { title: "", description: "" },
                      ],
                    },
                  });
                }}
              >
                <Plus size={16} className="mr-1" /> Add Point
              </Button>
            </div>
            <div className="space-y-6">
              {(formData.expertise?.mistakes || []).map((mistake, idx) => (
                <div
                  key={idx}
                  className="p-6 bg-slate-50 rounded-2xl border border-slate-200 relative space-y-4"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const currentMistakes = [
                        ...(formData.expertise?.mistakes || []),
                      ];
                      currentMistakes.splice(idx, 1);
                      setFormData({
                        ...formData,
                        expertise: {
                          ...(formData.expertise || {}),
                          mistakes: currentMistakes,
                        },
                      });
                    }}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                  >
                    <Trash2 size={18} />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Point Title</Label>
                      <Input
                        value={mistake.title || ""}
                        onChange={(e) => {
                          const currentMistakes = [
                            ...(formData.expertise?.mistakes || []),
                          ];
                          currentMistakes[idx] = {
                            ...currentMistakes[idx],
                            title: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            expertise: {
                              ...(formData.expertise || {}),
                              mistakes: currentMistakes,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Point Description</Label>
                      <Textarea
                        value={mistake.description || ""}
                        onChange={(e) => {
                          const currentMistakes = [
                            ...(formData.expertise?.mistakes || []),
                          ];
                          currentMistakes[idx] = {
                            ...currentMistakes[idx],
                            description: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            expertise: {
                              ...(formData.expertise || {}),
                              mistakes: currentMistakes,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 8: Brands */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="text-primary" size={24} />
            <h2 className="text-xl font-bold">
              Section 8: Brands We Have Built
            </h2>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-base font-bold">
                Select Brands from Portfolio
              </Label>
              {portfoliosLoading ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 className="animate-spin h-4 w-4" />
                  <span>Loading portfolios...</span>
                </div>
              ) : (
                <MultiSelect
                  options={portfolios}
                  value={portfolios
                    .filter((p) =>
                      (formData.section4_brands || []).some(
                        (b) => b.name === p.name || b.link === p.link,
                      ),
                    )
                    .map((p) => p.id)}
                  onChange={handlePortfolioChange}
                  placeholder="Select portfolio items to feature as brands..."
                />
              )}
            </div>

            {/* Selected Brands Grid Preview */}
            <div className="pt-4">
              <Label className="text-sm font-semibold text-slate-600 mb-3 block">
                Selected Brands ({formData.section4_brands?.length || 0})
              </Label>
              {formData.section4_brands &&
              formData.section4_brands.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {formData.section4_brands.map((brand) => (
                    <div
                      key={brand.name}
                      className="relative flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200"
                    >
                      {brand.image ? (
                        <img
                          src={brand.image}
                          alt={brand.name}
                          className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center text-slate-400">
                          <Briefcase size={20} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">
                          {brand.name}
                        </p>
                        {brand.link ? (
                          <a
                            href={brand.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline truncate block"
                          >
                            {brand.link}
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400 block">
                            No link specified
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePortfolio(brand.name)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full shrink-0"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl text-slate-400 text-sm">
                  No brands selected yet. Use the dropdown above to choose
                  brands.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 9: Case Studies */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="text-primary" size={24} />
            <h2 className="text-xl font-bold">
              Section 9: Case Studies We Have Built
            </h2>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-base font-bold">
                Select Case Studies from Portfolio
              </Label>
              {caseStudiesLoading ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 className="animate-spin h-4 w-4" />
                  <span>Loading case studies...</span>
                </div>
              ) : (
                <MultiSelect
                  options={caseStudies.map((cs) => ({
                    id: cs.id,
                    name:
                      cs.sort_data?.title || cs.banner?.title || cs.page_url,
                  }))}
                  value={caseStudies
                    .filter((cs) =>
                      (formData.section4_case_studies || []).some(
                        (c) => c.page_url === cs.page_url,
                      ),
                    )
                    .map((cs) => cs.id)}
                  onChange={handleCaseStudyChange}
                  placeholder="Select case studies to feature..."
                />
              )}
            </div>

            {/* Selected Case Studies Grid Preview */}
            <div className="pt-4">
              <Label className="text-sm font-semibold text-slate-600 mb-3 block">
                Selected Case Studies (
                {formData.section4_case_studies?.length || 0})
              </Label>
              {formData.section4_case_studies &&
              formData.section4_case_studies.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {formData.section4_case_studies.map((cs) => (
                    <div
                      key={cs.title}
                      className="relative flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200"
                    >
                      {cs.image ? (
                        <img
                          src={cs.image}
                          alt={cs.title}
                          className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center text-slate-400">
                          <Briefcase size={20} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">
                          {cs.title}
                        </p>
                        <span className="text-xs text-slate-500 block truncate">
                          /case-studies/{cs.page_url}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveCaseStudy(cs.title)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full shrink-0"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl text-slate-400 text-sm">
                  No case studies selected yet. Use the dropdown above to choose
                  case studies.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 10: Our Promise Section */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Section 10: Our Promise</h2>
          </div>
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={formData.our_promise?.title || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  our_promise: {
                    ...(formData.our_promise || {}),
                    title: e.target.value,
                  },
                })
              }
            />
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">Promises</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentPromises = formData.our_promise?.promises || [];
                  setFormData({
                    ...formData,
                    our_promise: {
                      ...(formData.our_promise || {}),
                      promises: [
                        ...currentPromises,
                        { image: "", title: "", copy: "" },
                      ],
                    },
                  });
                }}
              >
                <Plus size={16} className="mr-1" /> Add Promise
              </Button>
            </div>
            <div className="space-y-6">
              {(formData.our_promise?.promises || []).map((promise, idx) => (
                <div
                  key={idx}
                  className="p-6 bg-slate-50 rounded-2xl border border-slate-200 relative space-y-4"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const currentPromises = [
                        ...(formData.our_promise?.promises || []),
                      ];
                      currentPromises.splice(idx, 1);
                      setFormData({
                        ...formData,
                        our_promise: {
                          ...(formData.our_promise || {}),
                          promises: currentPromises,
                        },
                      });
                    }}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                  >
                    <Trash2 size={18} />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Promise Title</Label>
                      <Input
                        value={promise.title || ""}
                        onChange={(e) => {
                          const currentPromises = [
                            ...(formData.our_promise?.promises || []),
                          ];
                          currentPromises[idx] = {
                            ...currentPromises[idx],
                            title: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            our_promise: {
                              ...(formData.our_promise || {}),
                              promises: currentPromises,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Promise Description/Copy</Label>
                      <Textarea
                        value={promise.copy || ""}
                        onChange={(e) => {
                          const currentPromises = [
                            ...(formData.our_promise?.promises || []),
                          ];
                          currentPromises[idx] = {
                            ...currentPromises[idx],
                            copy: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            our_promise: {
                              ...(formData.our_promise || {}),
                              promises: currentPromises,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <ImageUpload
                      label="Promise Icon/Image"
                      value={promise.image || ""}
                      onChange={(url) => {
                        const currentPromises = [
                          ...(formData.our_promise?.promises || []),
                        ];
                        currentPromises[idx] = {
                          ...currentPromises[idx],
                          image: url,
                        };
                        setFormData({
                          ...formData,
                          our_promise: {
                            ...(formData.our_promise || {}),
                            promises: currentPromises,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 11: FAQ */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Section 11: FAQ</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-slate-100">
            <div className="space-y-2">
              <Label>Section Title</Label>
              <Input
                placeholder="e.g. Frequently Asked Questions"
                value={formData.section5_faq.title}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    section5_faq: {
                      ...formData.section5_faq,
                      title: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Section Description</Label>
              <Input
                placeholder="e.g. Find answers to common questions"
                value={formData.section5_faq.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    section5_faq: {
                      ...formData.section5_faq,
                      description: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">Questions & Answers</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setFormData({
                    ...formData,
                    section5_faq: {
                      ...formData.section5_faq,
                      questions: [
                        ...formData.section5_faq.questions,
                        { question: "", answer: "" },
                      ],
                    },
                  });
                }}
              >
                <Plus size={16} className="mr-1" /> Add FAQ
              </Button>
            </div>
            <div className="space-y-6">
              {formData.section5_faq.questions.map((faq, idx) => (
                <div
                  key={idx}
                  className="p-6 bg-slate-50 rounded-2xl border border-slate-100 relative space-y-4"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const newQuestions = [...formData.section5_faq.questions];
                      newQuestions.splice(idx, 1);
                      setFormData({
                        ...formData,
                        section5_faq: {
                          ...formData.section5_faq,
                          questions: newQuestions,
                        },
                      });
                    }}
                    className="absolute top-2 right-2 text-red-500"
                  >
                    <Trash2 size={18} />
                  </Button>
                  <div className="space-y-2">
                    <Label>Question</Label>
                    <Input
                      value={faq.question}
                      onChange={(e) => {
                        const newQuestions = [
                          ...formData.section5_faq.questions,
                        ];
                        newQuestions[idx] = {
                          ...newQuestions[idx],
                          question: e.target.value,
                        };
                        setFormData({
                          ...formData,
                          section5_faq: {
                            ...formData.section5_faq,
                            questions: newQuestions,
                          },
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Answer</Label>
                    <Textarea
                      value={faq.answer}
                      onChange={(e) => {
                        const newQuestions = [
                          ...formData.section5_faq.questions,
                        ];
                        newQuestions[idx] = {
                          ...newQuestions[idx],
                          answer: e.target.value,
                        };
                        setFormData({
                          ...formData,
                          section5_faq: {
                            ...formData.section5_faq,
                            questions: newQuestions,
                          },
                        });
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 12: Testimonial */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Quote className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Section 12: Testimonial</h2>
          </div>
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <Label htmlFor="testimonial_quote">Quote</Label>
              <Textarea
                id="testimonial_quote"
                placeholder="e.g. We spoke to four agencies before Five Online..."
                value={formData.testimonial?.quote || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    testimonial: {
                      ...formData.testimonial,
                      quote: e.target.value,
                    },
                  })
                }
                className="h-24"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="testimonial_author">Author</Label>
              <Input
                id="testimonial_author"
                placeholder="e.g. Founder, Premium Wellness Brand - D2C Growth Engine Client"
                value={formData.testimonial?.author || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    testimonial: {
                      ...formData.testimonial,
                      author: e.target.value,
                    },
                  })
                }
              />
            </div>
          </div>
        </div>

        {/* Section 13: Trusted Partners */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Section 13: Trusted Partners</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="trusted_partners_title">Section Title</Label>
              <Input
                id="trusted_partners_title"
                placeholder="e.g. Trusted by Ambitious Brands"
                value={formData.trusted_partners?.title || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    trusted_partners: {
                      ...formData.trusted_partners,
                      title: e.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trusted_partners_description">
                Section Description
              </Label>
              <Textarea
                id="trusted_partners_description"
                placeholder="e.g. From first-time D2C founders to enterprise brands..."
                value={formData.trusted_partners?.description || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    trusted_partners: {
                      ...formData.trusted_partners,
                      description: e.target.value,
                    },
                  })
                }
                className="h-20"
              />
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">Partner Logos</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentPartners =
                    formData.trusted_partners?.partners || [];
                  setFormData({
                    ...formData,
                    trusted_partners: {
                      ...formData.trusted_partners,
                      partners: [...currentPartners, { name: "", image: "" }],
                    },
                  });
                }}
              >
                <Plus size={16} className="mr-1" /> Add Partner
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {(formData.trusted_partners?.partners || []).map(
                (partner, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-50 rounded-xl border border-slate-200 relative space-y-4"
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const currentPartners = [
                          ...formData.trusted_partners.partners,
                        ];
                        currentPartners.splice(idx, 1);
                        setFormData({
                          ...formData,
                          trusted_partners: {
                            ...formData.trusted_partners,
                            partners: currentPartners,
                          },
                        });
                      }}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                    >
                      <Trash2 size={16} />
                    </Button>
                    <div className="space-y-2">
                      <Label>Partner Name</Label>
                      <Input
                        placeholder="e.g. TCS"
                        value={partner.name || ""}
                        onChange={(e) => {
                          const currentPartners = [
                            ...formData.trusted_partners.partners,
                          ];
                          currentPartners[idx] = {
                            ...currentPartners[idx],
                            name: e.target.value,
                          };
                          setFormData({
                            ...formData,
                            trusted_partners: {
                              ...formData.trusted_partners,
                              partners: currentPartners,
                            },
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <ImageUpload
                        label="Logo Image"
                        value={partner.image || ""}
                        onChange={(url) => {
                          const currentPartners = [
                            ...formData.trusted_partners.partners,
                          ];
                          currentPartners[idx] = {
                            ...currentPartners[idx],
                            image: url,
                          };
                          setFormData({
                            ...formData,
                            trusted_partners: {
                              ...formData.trusted_partners,
                              partners: currentPartners,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>

        {/* Section 14: CTA Section */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Section 14: CTA Section</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="cta_title">
                Title (supports HTML break &lt;br /&gt;)
              </Label>
              <Textarea
                id="cta_title"
                placeholder="e.g. Your D2C brand <br /> deserves to sell."
                value={formData.cta?.title || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    cta: { ...formData.cta, title: e.target.value },
                  })
                }
                className="h-20"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta_description">
                Description (supports HTML break &lt;br /&gt;)
              </Label>
              <Textarea
                id="cta_description"
                placeholder="e.g. Book a 30-minute strategy call..."
                value={formData.cta?.description || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    cta: { ...formData.cta, description: e.target.value },
                  })
                }
                className="h-20"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta_button_text">Button Text</Label>
              <Input
                id="cta_button_text"
                placeholder="e.g. Book A Strategy Call"
                value={formData.cta?.button_text || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    cta: { ...formData.cta, button_text: e.target.value },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta_button_link">Button Link</Label>
              <Input
                id="cta_button_link"
                placeholder="e.g. /contact"
                value={formData.cta?.button_link || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    cta: { ...formData.cta, button_link: e.target.value },
                  })
                }
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="cta_footer_note">Footer Note</Label>
              <Input
                id="cta_footer_note"
                placeholder="e.g. We take 4-6 new clients per quarter..."
                value={formData.cta?.footer_note || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    cta: { ...formData.cta, footer_note: e.target.value },
                  })
                }
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <ImageUpload
                label="Background Image"
                value={formData.cta?.bg_image || ""}
                onChange={(url) =>
                  setFormData({
                    ...formData,
                    cta: { ...formData.cta, bg_image: url },
                  })
                }
              />
            </div>
          </div>
        </div>

        {/* Custom Scripts */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Code className="text-primary" size={24} />
            <h2 className="text-xl font-bold">Custom Scripts</h2>
          </div>
          <div className="space-y-2">
            <Label htmlFor="custom_script">Add Header/Footer Scripts</Label>
            <Textarea
              id="custom_script"
              placeholder="e.g. <script>...</script>"
              value={formData.custom_script || ""}
              onChange={(e) =>
                setFormData({ ...formData, custom_script: e.target.value })
              }
              className="font-mono text-sm h-40"
            />
            <p className="text-xs text-slate-500">
              Warning: Enter valid script tags. These will be injected directly
              into the page.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PageForm;
